import type { WorkItem } from './types';

// The columns people expect from a ticket tool, mapped onto what the tracker
// actually stores. The one column those tools cannot express is To validate:
// shipped is not done here, because a bug waits for sightings on the fixed
// version to stay away before anyone calls it fixed.
export interface Column {
  id: string;
  label: string;
  hint: string;
  /** status written when a card is dropped here; a function because a bug that
   *  is code-complete is `fixed` while everything else is `built`. */
  statusFor: (item: WorkItem) => string;
  /** resolution written on drop, undefined means leave it alone */
  resolutionFor?: (item: WorkItem) => string | null;
}

export const COLUMNS: Column[] = [
  {
    id: 'backlog',
    label: 'Backlog',
    hint: 'not scheduled',
    statusFor: () => 'open',
    resolutionFor: () => null,
  },
  { id: 'next', label: 'Up next', hint: 'committed to soon', statusFor: () => 'planned', resolutionFor: () => null },
  { id: 'doing', label: 'In progress', hint: 'someone is on it', statusFor: () => 'in_progress', resolutionFor: () => null },
  {
    id: 'testing',
    label: 'To test',
    hint: 'code done, not shipped',
    statusFor: (i) => (i.kind === 'bug' ? 'fixed' : 'built'),
    resolutionFor: () => null,
  },
  { id: 'validating', label: 'To validate', hint: 'shipped, waiting for evidence', statusFor: () => 'released', resolutionFor: () => null },
  { id: 'done', label: 'Done', hint: 'confirmed', statusFor: () => 'done', resolutionFor: () => 'fixed' },
  { id: 'cancelled', label: 'Cancelled', hint: 'decided against', statusFor: () => 'done', resolutionFor: () => 'wont_fix' },
  { id: 'hold', label: 'On hold', hint: 'deferred with a reason', statusFor: () => 'parked', resolutionFor: () => null },
];

const CANCELLED = new Set(['wont_fix', 'duplicate', 'invalid']);

const BY_STATUS: Record<string, string> = {
  open: 'backlog',
  planned: 'next',
  in_progress: 'doing',
  built: 'testing',
  fixed: 'testing',
  released: 'validating',
  verified: 'done',
  done: 'done',
  parked: 'hold',
};

// A decision not to do something is an outcome, not open work, so it leaves
// whatever column its status would imply and shows as cancelled.
export function columnOf(item: WorkItem): string {
  if (item.resolution && CANCELLED.has(item.resolution)) return 'cancelled';
  return BY_STATUS[item.status] ?? 'backlog';
}

export function columnById(id: string): Column | undefined {
  return COLUMNS.find((c) => c.id === id);
}

/** True when a bug shipped but nothing has confirmed the fix yet. */
export function underWatch(item: WorkItem): boolean {
  return item.kind === 'bug' && item.status === 'released' && !item.resolution;
}
